import {
  Accordion,
  AccordionItem,
  Box,
  Button,
  DATE_FORMAT,
  Icon,
} from '@dev-spendesk/grapes';
import React from 'react';

import { ContactUs } from 'common/components/ContactUs';
import { useTranslation } from 'common/hooks/useTranslation';
import { type PriceMigrationPlan } from 'modules/company/billing/components/CompanyPlan/hooks/useGetBillingMigration/useGetBillingMigration';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

type Props = {
  migrationPlan: PriceMigrationPlan;
};

const reasonItems = [
  {
    id: 'reason1',
    key: 'billing.priceUpdateModal.annualPriceUpdate.whySection.reasons.item1' as const,
  },
  {
    id: 'reason2',
    key: 'billing.priceUpdateModal.annualPriceUpdate.whySection.reasons.item2' as const,
  },
  {
    id: 'reason3',
    key: 'billing.priceUpdateModal.annualPriceUpdate.whySection.reasons.item3' as const,
  },
  {
    id: 'reason4',
    key: 'billing.priceUpdateModal.annualPriceUpdate.whySection.reasons.item4' as const,
  },
  {
    id: 'reason5',
    key: 'billing.priceUpdateModal.annualPriceUpdate.whySection.reasons.item5' as const,
  },
];

const faqItems = [
  {
    id: 'faqItem1',
    questionKey:
      'billing.priceUpdateModal.annualPriceUpdate.faqSection.item1.question' as const,
    answerKey:
      'billing.priceUpdateModal.annualPriceUpdate.faqSection.item1.answer' as const,
  },
  {
    id: 'faqItem2',
    questionKey:
      'billing.priceUpdateModal.annualPriceUpdate.faqSection.item2.question' as const,
    answerKey:
      'billing.priceUpdateModal.annualPriceUpdate.faqSection.item2.answer' as const,
  },
  {
    id: 'faqItem3',
    questionKey:
      'billing.priceUpdateModal.annualPriceUpdate.faqSection.item3.question' as const,
    answerKey:
      'billing.priceUpdateModal.annualPriceUpdate.faqSection.item3.answer' as const,
  },
  {
    id: 'faqItem5',
    questionKey:
      'billing.priceUpdateModal.annualPriceUpdate.faqSection.item5.question' as const,
    answerKey:
      'billing.priceUpdateModal.annualPriceUpdate.faqSection.item5.answer' as const,
  },
  {
    id: 'faqItem6',
    questionKey:
      'billing.priceUpdateModal.annualPriceUpdate.faqSection.item6.question' as const,
    answerKey:
      'billing.priceUpdateModal.annualPriceUpdate.faqSection.item6.answer' as const,
  },
];

export const AnnualPricingUpdateContent = ({ migrationPlan }: Props) => {
  const { t, localeFormat } = useTranslation('global');
  const changeDate = new Date(migrationPlan.migrationDate);

  return (
    <div className="flex justify-center gap-48 px-[80px] py-16">
      <section className="w-[40vw] max-w-[1000px]">
        <h1 className="title-xxl mb-20">
          {t('billing.priceUpdateModal.annualPriceUpdate.title')}
        </h1>
        <p className="body-l">
          {t('billing.priceUpdateModal.annualPriceUpdate.description', {
            date: localeFormat(changeDate, DATE_FORMAT.CUSTOM, {
              dateStyle: 'medium',
              timeZone: 'Europe/Paris',
            }),
          })}
          <span className="text-content-brand-default">
            {' '}
            {t(
              'billing.priceUpdateModal.annualPriceUpdate.descriptionCommitment',
            )}
          </span>
        </p>
        <section className="mt-24">
          <h2 className="heading-l mb-16">
            {t('billing.priceUpdateModal.annualPriceUpdate.whySection.title')}
          </h2>
          <p className="text-content-secondary-bg-secondary mb-12">
            {t(
              'billing.priceUpdateModal.annualPriceUpdate.whySection.description',
            )}
          </p>
          <ul>
            {reasonItems.map((item) => (
              <li key={item.id} className="mt-8 flex items-center">
                <Icon
                  name="circle-check"
                  className="text-content-success-default mr-12 shrink-0"
                />
                {t(item.key)}
              </li>
            ))}
          </ul>
        </section>
        <section className="mt-24">
          <h2 className="heading-l mb-16">
            {t('billing.priceUpdateModal.annualPriceUpdate.faqSection.title')}
          </h2>
          <Accordion>
            {faqItems.map((faqItem) => (
              <AccordionItem
                key={faqItem.id}
                renderHeader={() => (
                  <div className="title-m">{t(faqItem.questionKey)}</div>
                )}
              >
                <div
                  className="text-content-secondary-bg-secondary px-24 py-12"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: t(faqItem.answerKey),
                  }}
                />
              </AccordionItem>
            ))}
          </Accordion>
        </section>
        <div className="rounded-8 bg-background-secondary-default mt-24 flex items-center p-16">
          <p className="title-m">
            {t('billing.priceUpdateModal.annualPriceUpdate.otherQuestion')}
          </p>
          <ContactUs>
            {({ handleContactUs }) => (
              <Button
                className="ml-auto whitespace-nowrap"
                variant="secondaryNeutral"
                text={t('helpCenter.contactUs')}
                onClick={() => {
                  handleContactUs();
                }}
              />
            )}
          </ContactUs>
        </div>
      </section>
      <section className="rounded-8 bg-background-secondary-default sticky top-0 h-fit w-[480px] min-w-[480px] p-24">
        <h3 className="title-l mb-20">{t('misc.details')}</h3>
        <div className="mb-20 flex justify-between">
          <span className="text-content-secondary-bg-secondary body-m">
            {t('billing.priceUpdateModal.annualPriceUpdate.currentPrice')}
          </span>
          <span className="title-xl font-bold">
            {formatMonetaryValue(migrationPlan.current)}
          </span>
        </div>
        <div className="mb-20 flex justify-between">
          <span className="text-content-secondary-bg-secondary">
            {t('billing.priceUpdateModal.annualPriceUpdate.newPrice')}
          </span>
          <span className="title-xl font-bold">
            {formatMonetaryValue(migrationPlan.estimate)}
          </span>
        </div>
        {migrationPlan.planItems && migrationPlan.planItems.length > 0 && (
          <Box variant="primary" className="mb-24 max-h-[250px] overflow-auto">
            <div className="text-content-primary title-m mb-16">
              {t('billing.priceUpdateModal.annualPriceUpdate.entityBreakdown')}
            </div>
            <ul className="flex flex-col gap-8">
              {migrationPlan.planItems.map((planItem) => (
                <li
                  key={planItem.companyId}
                  className="flex justify-between gap-4"
                >
                  <span className="text-content-secondary-bg-primary overflow-hidden text-nowrap text-ellipsis">
                    {planItem.companyName}
                  </span>
                  <span className="title-l">
                    {formatMonetaryValue(planItem.estimate)}
                  </span>
                </li>
              ))}
            </ul>
          </Box>
        )}
        <div>
          <span className="title-m">
            {t('billing.priceUpdateModal.annualPriceUpdate.note')}
          </span>
          <ul className="text-content-secondary-bg-secondary mt-8 list-disc px-24">
            <li>
              {t('billing.priceUpdateModal.annualPriceUpdate.priceIsEstimate')}
            </li>
            <li>
              {t('billing.priceUpdateModal.annualPriceUpdate.entitiesPrice')}
            </li>
            <li>
              {t(
                'billing.priceUpdateModal.annualPriceUpdate.priceIsWithoutVat',
              )}
            </li>
            {migrationPlan.planItems && migrationPlan.planItems.length > 0 && (
              <li>
                {t('billing.priceUpdateModal.annualPriceUpdate.conversion')}
              </li>
            )}
          </ul>
        </div>
        <div className="separator my-20" />
        <span className="title-m">
          {t('billing.priceUpdateModal.annualPriceUpdate.timeline')}
        </span>
        <div className="mt-8 flex justify-between gap-16">
          <Icon
            name="calendar-clock"
            size="l"
            className="text-content-brand-default"
          />
          <div className="w-fit">
            <span className="title-m">
              {localeFormat(changeDate, DATE_FORMAT.CUSTOM, {
                dateStyle: 'short',
                timeZone: 'Europe/Paris',
              })}
            </span>
            <p className="text-content-secondary-bg-secondary mt-8">
              {t('billing.priceUpdateModal.annualPriceUpdate.newPriceDate')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
